import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { ModeratorDashboard } from '../../features/topics/components/ModeratorDashboard';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../lib/api-client';
import { ExternalLink } from 'lucide-react';
import { getAppeals, reviewAppeal, type AppealWithDetails } from '../../services/moderation';
import { useToast } from '../../contexts/ToastContext';

interface Report {
  id: string;
  targetType: string;
  targetId: string;
  threadId: string;
  targetAuthorAlias: string;
  targetAuthorUserId: string;
  reason: string;
  reporterAlias: string;
  status: string;
  createdAt: string;
  resolvedAt?: string;
  resolution?: string;
}

interface Thread {
  id: string;
  topicId: string;
  title: string;
  authorAlias: string;
  createdAt: string;
  pinned: boolean;
  locked: boolean;
  hidden?: boolean;
}

interface Post {
  id: string;
  threadId: string;
  authorAlias: string;
  body: string;
  hidden: boolean;
  createdAt: string;
}

interface User {
  id: string;
  username: string;
  displayName: string;
  email?: string;
  status: string;
  role: string;
  mutedUntil?: string;
  createdAt: string;
  updatedAt: string;
}

interface ModerationAction {
  id: number;
  userId: string;
  adminId: string;
  action: string;
  resourceType: string;
  resourceId: string;
  details?: string;
  createdAt: string;
}

export function ModerationPage() {
  const { user, logout } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get current tab from URL, default to 'reports'
  const view = (searchParams.get('tab') as 'reports' | 'threads' | 'user-moderation' | 'hidden-content' | 'banned-users' | 'muted-users' | 'appeals' | 'action-history') || 'reports';

  // Function to change tab via URL
  const setView = (tab: 'reports' | 'threads' | 'user-moderation' | 'hidden-content' | 'banned-users' | 'muted-users' | 'appeals' | 'action-history') => {
    setSearchParams({ tab });
  };

  // User is already authenticated and authorized by ProtectedRoute with role check
  // So we can directly use the user info from context

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      {/* Header */}
      <div className="bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Moderation Dashboard</h1>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Moderator: {user?.username} | Role: {user?.role}
            </p>
          </div>
          <Button
            onClick={logout}
            variant="secondary"
          >
            Sign Out
          </Button>
        </div>
      </div>

      {/* Navigation */}
      <div className="border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-4">
            <button
              onClick={() => setView('reports')}
              className={`px-4 py-3 border-b-2 transition-colors ${
                view === 'reports'
                  ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                  : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-300'
              }`}
            >
              Reports
            </button>
            <button
              onClick={() => setView('threads')}
              className={`px-4 py-3 border-b-2 transition-colors ${
                view === 'threads'
                  ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                  : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-300'
              }`}
            >
              Thread Management
            </button>
            <button
              onClick={() => setView('user-moderation')}
              className={`px-4 py-3 border-b-2 transition-colors ${
                view === 'user-moderation'
                  ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                  : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-300'
              }`}
            >
              User Moderation
            </button>
            <button
              onClick={() => setView('hidden-content')}
              className={`px-4 py-3 border-b-2 transition-colors ${
                view === 'hidden-content'
                  ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                  : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-300'
              }`}
            >
              Hidden Content
            </button>
            <button
              onClick={() => setView('banned-users')}
              className={`px-4 py-3 border-b-2 transition-colors ${
                view === 'banned-users'
                  ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                  : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-300'
              }`}
            >
              Banned Users
            </button>
            <button
              onClick={() => setView('muted-users')}
              className={`px-4 py-3 border-b-2 transition-colors ${
                view === 'muted-users'
                  ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                  : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-300'
              }`}
            >
              Muted Users
            </button>
            <button
              onClick={() => setView('appeals')}
              className={`px-4 py-3 border-b-2 transition-colors ${
                view === 'appeals'
                  ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                  : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-300'
              }`}
            >
              Appeals
            </button>
            <button
              onClick={() => setView('action-history')}
              className={`px-4 py-3 border-b-2 transition-colors ${
                view === 'action-history'
                  ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                  : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-300'
              }`}
            >
              Action History
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {view === 'reports' && <ReportsView />}
        {view === 'threads' && <ThreadManagementView />}
        {view === 'user-moderation' && <ModeratorDashboard />}
        {view === 'hidden-content' && <HiddenContentView />}
        {view === 'banned-users' && <BannedUsersView />}
        {view === 'muted-users' && <MutedUsersView />}
        {view === 'appeals' && <AppealsView />}
        {view === 'action-history' && <ActionHistoryView />}
      </div>
    </div>
  );
}

function ReportsView() {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [reports, setReports] = useState<Report[]>([]);
  const [filter, setFilter] = useState<'all' | 'open' | 'resolved'>('open');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadReports();
  }, [filter]);

  async function loadReports() {
    try {
      setLoading(true);
      setError(null);
      const statusParam = filter === 'all' ? '' : filter;
      const data = await api.get<Report[]>(
        `/forum/reports?status=${statusParam}&limit=100`
      );
      setReports(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }

  async function handleResolve(reportId: string, resolution: string) {
    try {
      await api.post(`/forum/reports/${reportId}/resolve`, {
        status: 'resolved',
        note: resolution,
      });
      showSuccess('Report resolved successfully');
      loadReports();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to resolve report');
    }
  }

  if (loading) {
    return <div className="text-center text-slate-600 dark:text-slate-400">Loading reports...</div>;
  }

  if (error) {
    return (
      <div className="bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-900 rounded-lg p-4 text-red-700 dark:text-red-400">
        Error: {error}
        <Button onClick={loadReports} className="mt-4">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div>
      {/* Filter */}
      <div className="mb-6 flex gap-3">
        <Button
          onClick={() => setFilter('open')}
          className={filter === 'open' ? 'bg-brand-600' : ''}
          variant={filter === 'open' ? undefined : 'secondary'}
        >
          Open ({reports.filter((r) => r.status === 'open').length})
        </Button>
        <Button
          onClick={() => setFilter('resolved')}
          className={filter === 'resolved' ? 'bg-brand-600' : ''}
          variant={filter === 'resolved' ? undefined : 'secondary'}
        >
          Resolved
        </Button>
        <Button
          onClick={() => setFilter('all')}
          className={filter === 'all' ? 'bg-brand-600' : ''}
          variant={filter === 'all' ? undefined : 'secondary'}
        >
          All
        </Button>
      </div>

      {/* Reports List */}
      {reports.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-8 text-center text-slate-600 dark:text-slate-400">
          No {filter !== 'all' ? filter : ''} reports found
        </div>
      ) : (
        <div className="space-y-4">
          {reports.map((report) => (
            <ReportCard
              key={report.id}
              report={report}
              onResolve={handleResolve}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ReportCard({
  report,
  onResolve,
}: {
  report: Report;
  onResolve: (reportId: string, resolution: string) => void;
}) {
  const [showResolve, setShowResolve] = useState(false);
  const [resolution, setResolution] = useState('');
  const [showMuteModal, setShowMuteModal] = useState(false);
  const [muteDuration, setMuteDuration] = useState('24');
  const [showBanModal, setShowBanModal] = useState(false);
  const navigate = useNavigate();
  const { showSuccess, showError, showWarning } = useToast();

  const handleViewContent = () => {
    // Both thread and post reports now have threadId
    if (report.threadId) {
      // Open thread in new tab
      window.open(`/forum/thread/${report.threadId}`, '_blank');
    } else {
      showWarning('Thread information not available for this report');
    }
  };

  const handleBanUser = async () => {
    try {
      // Use user ID if available, otherwise use alias
      if (report.targetAuthorUserId) {
        await api.post(`/moderation/users/${report.targetAuthorUserId}/ban`, {});
      } else {
        await api.post(`/moderation/users/by-alias/${encodeURIComponent(report.targetAuthorAlias)}/ban`, {});
      }
      showSuccess(`User ${report.targetAuthorAlias} has been banned`);
      setShowBanModal(false);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to ban user');
    }
  };

  const handleMuteUser = async () => {
    const durationHours = parseInt(muteDuration, 10);
    if (isNaN(durationHours) || durationHours <= 0) {
      showError('Please enter a valid number of hours');
      return;
    }

    try {
      // Use user ID if available, otherwise use alias
      if (report.targetAuthorUserId) {
        await api.post(`/moderation/users/${report.targetAuthorUserId}/mute`, {
          durationHours,
        });
      } else {
        await api.post(`/moderation/users/by-alias/${encodeURIComponent(report.targetAuthorAlias)}/mute`, {
          durationHours,
        });
      }
      showSuccess(`User ${report.targetAuthorAlias} has been muted for ${durationHours} hours`);
      setShowMuteModal(false);
      setMuteDuration('24');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to mute user');
    }
  };

  const handleHideContent = async () => {
    if (!confirm(`Are you sure you want to hide this ${report.targetType}?`)) {
      return;
    }

    try {
      if (report.targetType === 'post') {
        await api.post(`/moderation/posts/${report.targetId}/hide`, {});
      } else if (report.targetType === 'thread') {
        await api.post(`/moderation/threads/${report.targetId}/hide`, {});
      }
      showSuccess(`${report.targetType} has been hidden`);
    } catch (err) {
      showError(err instanceof Error ? err.message : `Failed to hide ${report.targetType}`);
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span
              className={`px-2 py-1 rounded text-xs font-medium ${
                report.status === 'open'
                  ? 'bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-800 text-yellow-700 dark:text-yellow-400'
                  : 'bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-800 text-green-700 dark:text-green-400'
              }`}
            >
              {report.status}
            </span>
            <span className="px-2 py-1 bg-slate-100 dark:bg-slate-700 rounded text-xs text-slate-700 dark:text-slate-300">
              {report.targetType}
            </span>
            <button
              onClick={handleViewContent}
              className="flex items-center gap-1 px-2 py-1 bg-brand-100 dark:bg-brand-900/30 border border-brand-300 dark:border-brand-800 text-brand-700 dark:text-brand-400 hover:bg-brand-200 dark:hover:bg-brand-900/50 rounded text-xs transition-colors"
              title="View reported content"
            >
              <ExternalLink className="w-3 h-3" />
              <span>View Content</span>
            </button>
          </div>
          <div className="text-sm text-slate-600 dark:text-slate-400 mb-2">
            Reported by: <span className="text-slate-900 dark:text-slate-300">{report.reporterAlias}</span>
          </div>
          <div className="text-sm text-slate-600 dark:text-slate-400 mb-2">
            Content author: <span className="text-slate-900 dark:text-slate-300 font-medium">{report.targetAuthorAlias}</span>
          </div>
          <div className="text-sm text-slate-600 dark:text-slate-400 mb-2">
            Author user ID: <span className="text-slate-500 dark:text-slate-500 font-mono text-xs">{report.targetAuthorUserId}</span>
          </div>
          <div className="text-sm text-slate-600 dark:text-slate-400">
            Target ID: <span className="text-slate-500 dark:text-slate-500 font-mono text-xs">{report.targetId}</span>
          </div>
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-500">
          {new Date(report.createdAt).toLocaleString()}
        </div>
      </div>

      <div className="bg-slate-50 dark:bg-slate-900 rounded p-4 mb-4">
        <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Reason:</div>
        <div className="text-slate-900 dark:text-slate-100">{report.reason}</div>
      </div>

      {report.status === 'open' && !showResolve && (
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => setShowResolve(true)} className="bg-green-600 hover:bg-green-700">
            Resolve Report
          </Button>
          <Button onClick={handleHideContent} className="bg-red-600 hover:bg-red-700">
            Hide Content
          </Button>
          <Button onClick={() => setShowMuteModal(true)} className="bg-yellow-600 hover:bg-yellow-700">
            Mute User
          </Button>
          <Button onClick={() => setShowBanModal(true)} className="bg-orange-600 hover:bg-orange-700">
            Ban User
          </Button>
        </div>
      )}

      {/* Mute User Modal */}
      {showMuteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                Mute User
              </h2>
              <button
                onClick={() => {
                  setShowMuteModal(false);
                  setMuteDuration('24');
                }}
                className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-300"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                You are about to mute user: <span className="font-semibold text-slate-900 dark:text-slate-200">{report.targetAuthorAlias}</span>
              </p>

              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Mute Duration (hours) *
              </label>
              <input
                type="number"
                value={muteDuration}
                onChange={(e) => setMuteDuration(e.target.value)}
                min="1"
                className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                placeholder="24"
              />
              <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                Common durations: 24 hours (1 day), 168 hours (1 week), 720 hours (30 days)
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleMuteUser}
                className="flex-1 bg-yellow-600 hover:bg-yellow-700"
              >
                Confirm Mute
              </Button>
              <Button
                onClick={() => {
                  setShowMuteModal(false);
                  setMuteDuration('24');
                }}
                variant="secondary"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Ban User Modal */}
      {showBanModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <span className="text-orange-600 dark:text-orange-400">⚠️</span>
                Ban User
              </h2>
              <button
                onClick={() => setShowBanModal(false)}
                className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-300"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="mb-6">
              <p className="text-slate-700 dark:text-slate-300 mb-3">
                Are you sure you want to ban user{' '}
                <span className="font-semibold text-slate-900 dark:text-slate-100">{report.targetAuthorAlias}</span>?
              </p>
              <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-900 rounded-lg p-3">
                <p className="text-sm text-orange-800 dark:text-orange-300 font-medium mb-1">
                  ⚠️ Warning: This is a permanent action
                </p>
                <p className="text-xs text-orange-700 dark:text-orange-400">
                  This user will be permanently banned from the forum and will not be able to post, comment, or participate in any discussions.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleBanUser}
                className="flex-1 bg-orange-600 hover:bg-orange-700"
              >
                Yes, Ban User
              </Button>
              <Button
                onClick={() => setShowBanModal(false)}
                variant="secondary"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {showResolve && (
        <div className="space-y-3">
          <textarea
            value={resolution}
            onChange={(e) => setResolution(e.target.value)}
            placeholder="Enter resolution notes..."
            className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            rows={3}
          />
          <div className="flex gap-2">
            <Button
              onClick={() => {
                if (resolution.trim()) {
                  onResolve(report.id, resolution);
                  setShowResolve(false);
                  setResolution('');
                }
              }}
              className="bg-green-600 hover:bg-green-700"
            >
              Confirm Resolution
            </Button>
            <Button
              onClick={() => {
                setShowResolve(false);
                setResolution('');
              }}
              variant="secondary"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {report.resolution && (
        <div className="mt-4 bg-green-100 dark:bg-green-900/20 border border-green-300 dark:border-green-900 rounded p-3">
          <div className="text-xs font-medium text-green-700 dark:text-green-400 mb-1">Resolution:</div>
          <div className="text-sm text-slate-900 dark:text-slate-300">{report.resolution}</div>
        </div>
      )}
    </div>
  );
}

function ThreadManagementView() {
  const [topicId, setTopicId] = useState('');
  const [threads, setThreads] = useState<Thread[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showSuccess, showError } = useToast();

  async function loadThreads() {
    if (!topicId.trim()) return;

    try {
      setLoading(true);
      setError(null);
      const data = await api.get<Thread[]>(`/forum/topics/${topicId}/threads?limit=50`);
      setThreads(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load threads');
    } finally {
      setLoading(false);
    }
  }

  async function updateThread(threadId: string, updates: { pinned?: boolean; locked?: boolean }) {
    try {
      await api.patch(`/forum/threads/${threadId}`, updates);
      showSuccess('Thread updated successfully');
      loadThreads();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update thread');
    }
  }

  return (
    <div>
      <div className="mb-6">
        <div className="flex gap-3">
          <input
            type="text"
            value={topicId}
            onChange={(e) => setTopicId(e.target.value)}
            placeholder="Enter topic ID or slug"
            className="flex-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded px-4 py-3 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          />
          <Button onClick={loadThreads} disabled={!topicId.trim()}>
            Load Threads
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-900 rounded-lg p-4 text-red-700 dark:text-red-400 mb-6">
          {error}
        </div>
      )}

      {loading && <div className="text-center text-slate-600 dark:text-slate-400">Loading threads...</div>}

      {!loading && threads.length === 0 && topicId && (
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-8 text-center text-slate-600 dark:text-slate-400">
          No threads found
        </div>
      )}

      {threads.length > 0 && (
        <div className="space-y-3">
          {threads.map((thread) => (
            <div key={thread.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {thread.pinned && (
                      <span className="px-2 py-0.5 bg-brand-100 dark:bg-brand-900/30 border border-brand-300 dark:border-brand-800 rounded text-xs text-brand-700 dark:text-brand-400">
                        Pinned
                      </span>
                    )}
                    {thread.locked && (
                      <span className="px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-800 rounded text-xs text-yellow-700 dark:text-yellow-400">
                        Locked
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1">{thread.title}</h3>
                  <div className="text-sm text-slate-600 dark:text-slate-400">
                    by {thread.authorAlias} • {new Date(thread.createdAt).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => updateThread(thread.id, { pinned: !thread.pinned })}
                    className={thread.pinned ? 'bg-brand-600' : ''}
                    variant={thread.pinned ? undefined : 'secondary'}
                  >
                    {thread.pinned ? 'Unpin' : 'Pin'}
                  </Button>
                  <Button
                    onClick={() => updateThread(thread.id, { locked: !thread.locked })}
                    className={thread.locked ? 'bg-yellow-600' : ''}
                    variant={thread.locked ? undefined : 'secondary'}
                  >
                    {thread.locked ? 'Unlock' : 'Lock'}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function HiddenContentView() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [threads, setThreads] = useState<Thread[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'posts' | 'threads'>('posts');
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    loadHiddenContent();
  }, []);

  async function loadHiddenContent() {
    try {
      setLoading(true);
      setError(null);
      const [postsData, threadsData] = await Promise.all([
        api.get<Post[]>('/moderation/hidden-posts?limit=100'),
        api.get<Thread[]>('/moderation/hidden-threads?limit=100'),
      ]);
      setPosts(postsData || []);
      setThreads(threadsData || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load hidden content');
    } finally {
      setLoading(false);
    }
  }

  async function handleRestorePost(postId: string) {
    if (!confirm('Are you sure you want to restore this post?')) {
      return;
    }

    try {
      await api.post(`/moderation/posts/${postId}/unhide`, {});
      showSuccess('Post has been restored');
      loadHiddenContent(); // Refresh the list
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to restore post');
    }
  }

  async function handleRestoreThread(threadId: string) {
    if (!confirm('Are you sure you want to restore this thread?')) {
      return;
    }

    try {
      await api.post(`/moderation/threads/${threadId}/unhide`, {});
      showSuccess('Thread has been restored');
      loadHiddenContent(); // Refresh the list
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to restore thread');
    }
  }

  if (loading) {
    return <div className="text-center text-slate-600 dark:text-slate-400">Loading hidden content...</div>;
  }

  if (error) {
    return (
      <div className="bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-900 rounded-lg p-4 text-red-700 dark:text-red-400">
        Error: {error}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex gap-3">
        <Button
          onClick={() => setActiveTab('posts')}
          className={activeTab === 'posts' ? 'bg-brand-600' : ''}
          variant={activeTab === 'posts' ? undefined : 'secondary'}
        >
          Hidden Posts ({posts.length})
        </Button>
        <Button
          onClick={() => setActiveTab('threads')}
          className={activeTab === 'threads' ? 'bg-brand-600' : ''}
          variant={activeTab === 'threads' ? undefined : 'secondary'}
        >
          Hidden Threads ({threads.length})
        </Button>
      </div>

      {activeTab === 'posts' && (
        <div className="space-y-3">
          {posts.length === 0 ? (
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-8 text-center text-slate-600 dark:text-slate-400">
              No hidden posts
            </div>
          ) : (
            posts.map((post) => (
              <div key={post.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="text-sm text-slate-600 dark:text-slate-400">
                    by {post.authorAlias} • {new Date(post.createdAt).toLocaleString()}
                  </div>
                  <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-800 rounded text-xs text-red-700 dark:text-red-400">
                    Hidden
                  </span>
                </div>
                <div className="text-slate-900 dark:text-slate-300 text-sm mb-3">{post.body}</div>
                <div className="text-xs text-slate-500 dark:text-slate-500 mb-3">Post ID: {post.id}</div>
                <div className="flex gap-2">
                  <button
                    onClick={() => window.open(`/forum/thread/${post.threadId}`, '_blank')}
                    className="flex items-center gap-1 px-3 py-1.5 bg-brand-100 dark:bg-brand-900/30 border border-brand-300 dark:border-brand-800 text-brand-700 dark:text-brand-400 hover:bg-brand-200 dark:hover:bg-brand-900/50 rounded text-sm transition-colors"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    View Thread
                  </button>
                  <Button
                    onClick={() => handleRestorePost(post.id)}
                    className="bg-green-600 hover:bg-green-700 text-sm py-1.5"
                  >
                    Restore Post
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'threads' && (
        <div className="space-y-3">
          {threads.length === 0 ? (
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-8 text-center text-slate-600 dark:text-slate-400">
              No hidden threads
            </div>
          ) : (
            threads.map((thread) => (
              <div key={thread.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{thread.title}</h3>
                  <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-800 rounded text-xs text-red-700 dark:text-red-400">
                    Hidden
                  </span>
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                  by {thread.authorAlias} • {new Date(thread.createdAt).toLocaleDateString()}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-500 mb-3">Thread ID: {thread.id}</div>
                <div className="flex gap-2">
                  <button
                    onClick={() => window.open(`/forum/thread/${thread.id}`, '_blank')}
                    className="flex items-center gap-1 px-3 py-1.5 bg-brand-100 dark:bg-brand-900/30 border border-brand-300 dark:border-brand-800 text-brand-700 dark:text-brand-400 hover:bg-brand-200 dark:hover:bg-brand-900/50 rounded text-sm transition-colors"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    View Thread
                  </button>
                  <Button
                    onClick={() => handleRestoreThread(thread.id)}
                    className="bg-green-600 hover:bg-green-700 text-sm py-1.5"
                  >
                    Restore Thread
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function BannedUsersView() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unbanUser, setUnbanUser] = useState<{ id: string; username: string } | null>(null);
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    loadBannedUsers();
  }, []);

  async function loadBannedUsers() {
    try {
      setLoading(true);
      setError(null);
      const data = await api.get<User[]>('/moderation/banned-users?limit=100');
      setUsers(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load banned users');
    } finally {
      setLoading(false);
    }
  }

  async function confirmUnbanUser() {
    if (!unbanUser) return;

    setError(null);

    try {
      await api.post(`/moderation/users/${unbanUser.id}/unban`, {});
      showSuccess(`${unbanUser.username} has been unbanned successfully`);
      setUnbanUser(null);
      loadBannedUsers(); // Refresh the list
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to unban user');
    }
  }

  if (loading) {
    return <div className="text-center text-slate-600 dark:text-slate-400">Loading banned users...</div>;
  }

  if (error) {
    return (
      <div className="bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-900 rounded-lg p-4 text-red-700 dark:text-red-400">
        Error: {error}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 text-slate-600 dark:text-slate-400">
        Total banned users: <span className="text-slate-900 dark:text-slate-200 font-semibold">{users.length}</span>
      </div>

      {users.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-8 text-center text-slate-600 dark:text-slate-400">
          No banned users
        </div>
      ) : (
        <div className="space-y-3">
          {users.map((user) => (
            <div key={user.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{user.displayName}</h3>
                    <span className="px-2 py-1 bg-orange-100 dark:bg-orange-900/30 border border-orange-300 dark:border-orange-800 rounded text-xs text-orange-700 dark:text-orange-400">
                      Banned
                    </span>
                  </div>
                  <div className="text-sm text-slate-600 dark:text-slate-400 mb-1">
                    Username: <span className="text-slate-900 dark:text-slate-300">{user.username}</span>
                  </div>
                  <div className="text-sm text-slate-600 dark:text-slate-400 mb-1">
                    Role: <span className="text-slate-900 dark:text-slate-300">{user.role}</span>
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-500">
                    User ID: {user.id}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="text-xs text-slate-500 dark:text-slate-500">
                    Banned: {new Date(user.updatedAt).toLocaleString()}
                  </div>
                  <Button
                    onClick={() => setUnbanUser({ id: user.id, username: user.username })}
                    className="bg-green-600 hover:bg-green-700 text-sm py-1.5 px-3"
                  >
                    Unban
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Unban Confirmation Modal */}
      {unbanUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                Confirm Unban
              </h2>
              <button
                onClick={() => setUnbanUser(null)}
                className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-300"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="mb-6">
              <p className="text-slate-700 dark:text-slate-300">
                Are you sure you want to unban user{' '}
                <span className="font-semibold text-slate-900 dark:text-slate-100">{unbanUser.username}</span>?
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-500 mt-2">
                This will restore their access to the forum and allow them to post and participate in discussions again.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={confirmUnbanUser}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                Yes, Unban User
              </Button>
              <Button
                onClick={() => setUnbanUser(null)}
                variant="secondary"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MutedUsersView() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unmuteUser, setUnmuteUser] = useState<{ id: string; username: string } | null>(null);
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    loadMutedUsers();
  }, []);

  async function loadMutedUsers() {
    try {
      setLoading(true);
      setError(null);
      const data = await api.get<User[]>('/moderation/muted-users?limit=100');
      setUsers(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load muted users');
    } finally {
      setLoading(false);
    }
  }

  async function confirmUnmuteUser() {
    if (!unmuteUser) return;

    setError(null);

    try {
      await api.post(`/moderation/users/${unmuteUser.id}/unmute`, {});
      showSuccess(`${unmuteUser.username} has been unmuted successfully`);
      setUnmuteUser(null);
      loadMutedUsers(); // Refresh the list
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to unmute user');
    }
  }

  if (loading) {
    return <div className="text-center text-slate-600 dark:text-slate-400">Loading muted users...</div>;
  }

  if (error) {
    return (
      <div className="bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-900 rounded-lg p-4 text-red-700 dark:text-red-400">
        Error: {error}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 text-slate-600 dark:text-slate-400">
        Currently muted users: <span className="text-slate-900 dark:text-slate-200 font-semibold">{users.length}</span>
      </div>

      {users.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-8 text-center text-slate-600 dark:text-slate-400">
          No muted users
        </div>
      ) : (
        <div className="space-y-3">
          {users.map((user) => (
            <div key={user.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{user.displayName}</h3>
                    <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-800 rounded text-xs text-yellow-700 dark:text-yellow-400">
                      Muted
                    </span>
                  </div>
                  <div className="text-sm text-slate-600 dark:text-slate-400 mb-1">
                    Username: <span className="text-slate-900 dark:text-slate-300">{user.username}</span>
                  </div>
                  <div className="text-sm text-slate-600 dark:text-slate-400 mb-1">
                    Muted until: <span className="text-slate-900 dark:text-slate-300">
                      {user.mutedUntil ? new Date(user.mutedUntil).toLocaleString() : 'N/A'}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-500">
                    User ID: {user.id}
                  </div>
                </div>
                <Button
                  onClick={() => setUnmuteUser({ id: user.id, username: user.username })}
                  className="bg-green-600 hover:bg-green-700 text-sm py-1.5 px-3"
                >
                  Unmute
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Unmute Confirmation Modal */}
      {unmuteUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                Confirm Unmute
              </h2>
              <button
                onClick={() => setUnmuteUser(null)}
                className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-300"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="mb-6">
              <p className="text-slate-700 dark:text-slate-300">
                Are you sure you want to unmute user{' '}
                <span className="font-semibold text-slate-900 dark:text-slate-100">{unmuteUser.username}</span>?
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-500 mt-2">
                This will allow them to post and participate in discussions again.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={confirmUnmuteUser}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                Yes, Unmute User
              </Button>
              <Button
                onClick={() => setUnmuteUser(null)}
                variant="secondary"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ActionHistoryView() {
  const [actions, setActions] = useState<ModerationAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const [actionTypeFilter, setActionTypeFilter] = useState<string>('');
  const [moderatorIdFilter, setModeratorIdFilter] = useState<string>('');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');

  useEffect(() => {
    loadActionHistory();
  }, [actionTypeFilter, moderatorIdFilter, dateFromFilter, dateToFilter]);

  async function loadActionHistory() {
    try {
      setLoading(true);
      setError(null);

      // Build query params
      const params = new URLSearchParams();
      if (actionTypeFilter) params.set('actionType', actionTypeFilter);
      if (moderatorIdFilter) params.set('moderatorId', moderatorIdFilter);
      if (dateFromFilter) params.set('dateFrom', new Date(dateFromFilter).toISOString());
      if (dateToFilter) params.set('dateTo', new Date(dateToFilter).toISOString());

      const url = `/moderation/audit-logs${params.toString() ? '?' + params.toString() : ''}`;
      const response = await api.get<{ logs: ModerationAction[] }>(url);
      setActions(response.logs || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load action history');
    } finally {
      setLoading(false);
    }
  }

  async function handleExport(format: 'csv' | 'json') {
    try {
      // Build query params with filters
      const params = new URLSearchParams();
      params.set('format', format);
      if (actionTypeFilter) params.set('actionType', actionTypeFilter);
      if (moderatorIdFilter) params.set('moderatorId', moderatorIdFilter);
      if (dateFromFilter) params.set('dateFrom', new Date(dateFromFilter).toISOString());
      if (dateToFilter) params.set('dateTo', new Date(dateToFilter).toISOString());

      const url = `/moderation/audit-logs/export?${params.toString()}`;

      // Create a temporary link to download the file (relative URL)
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit_logs.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export audit logs');
    }
  }

  function clearFilters() {
    setActionTypeFilter('');
    setModeratorIdFilter('');
    setDateFromFilter('');
    setDateToFilter('');
  }

  const getActionColor = (action: string) => {
    switch (action.toLowerCase()) {
      case 'ban':
      case 'ban_user':
        return 'bg-orange-100 dark:bg-orange-900/30 border-orange-300 dark:border-orange-800 text-orange-700 dark:text-orange-400';
      case 'mute':
      case 'mute_user':
        return 'bg-yellow-100 dark:bg-yellow-900/30 border-yellow-300 dark:border-yellow-800 text-yellow-700 dark:text-yellow-400';
      case 'hide':
      case 'hide_content':
        return 'bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-800 text-red-700 dark:text-red-400';
      case 'unban':
      case 'unban_user':
        return 'bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-800 text-green-700 dark:text-green-400';
      case 'unmute':
      case 'unmute_user':
        return 'bg-brand-100 dark:bg-brand-900/30 border-brand-300 dark:border-brand-800 text-brand-700 dark:text-brand-400';
      default:
        return 'bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300';
    }
  };

  return (
    <div>
      {/* Filters Section */}
      <div className="mb-6 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-200">Filters</h3>
          <button
            onClick={clearFilters}
            className="text-sm text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 transition-colors"
          >
            Clear All
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Action Type Filter */}
          <div>
            <label className="block text-sm text-slate-700 dark:text-slate-400 mb-2">Action Type</label>
            <select
              value={actionTypeFilter}
              onChange={(e) => setActionTypeFilter(e.target.value)}
              className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            >
              <option value="">All Actions</option>
              <option value="ban_user">Ban User</option>
              <option value="mute_user">Mute User</option>
              <option value="unban_user">Unban User</option>
              <option value="unmute_user">Unmute User</option>
              <option value="hide_content">Hide Content</option>
              <option value="approve_appeal">Approve Appeal</option>
              <option value="reject_appeal">Reject Appeal</option>
            </select>
          </div>

          {/* Moderator ID Filter */}
          <div>
            <label className="block text-sm text-slate-700 dark:text-slate-400 mb-2">Moderator ID</label>
            <input
              type="text"
              value={moderatorIdFilter}
              onChange={(e) => setModeratorIdFilter(e.target.value)}
              placeholder="Enter moderator ID"
              className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-slate-200 placeholder-slate-500 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            />
          </div>

          {/* Date From Filter */}
          <div>
            <label className="block text-sm text-slate-700 dark:text-slate-400 mb-2">Date From</label>
            <input
              type="date"
              value={dateFromFilter}
              onChange={(e) => setDateFromFilter(e.target.value)}
              className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            />
          </div>

          {/* Date To Filter */}
          <div>
            <label className="block text-sm text-slate-700 dark:text-slate-400 mb-2">Date To</label>
            <input
              type="date"
              value={dateToFilter}
              onChange={(e) => setDateToFilter(e.target.value)}
              className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Export Buttons */}
      <div className="mb-4 flex items-center justify-between">
        <div className="text-slate-600 dark:text-slate-400">
          Total actions: <span className="text-slate-900 dark:text-slate-200 font-semibold">{actions.length}</span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => handleExport('csv')}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm"
          >
            Export CSV
          </button>
          <button
            onClick={() => handleExport('json')}
            className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition-colors text-sm"
          >
            Export JSON
          </button>
        </div>
      </div>

      {/* Loading/Error States */}
      {loading && (
        <div className="text-center text-slate-600 dark:text-slate-400 py-8">Loading action history...</div>
      )}

      {error && (
        <div className="bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-900 rounded-lg p-4 text-red-700 dark:text-red-400 mb-4">
          Error: {error}
        </div>
      )}

      {/* Actions List */}
      {!loading && !error && (
        <>
          {actions.length === 0 ? (
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-8 text-center text-slate-600 dark:text-slate-400">
              No moderation actions recorded
            </div>
          ) : (
            <div className="space-y-3">
              {actions.map((action) => (
                <div key={action.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 border rounded text-xs font-medium ${getActionColor(action.action)}`}>
                        {action.action.toUpperCase()}
                      </span>
                      {action.resourceType && (
                        <span className="px-2 py-1 bg-slate-100 dark:bg-slate-700 rounded text-xs text-slate-700 dark:text-slate-300">
                          {action.resourceType}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-500">
                      {new Date(action.createdAt).toLocaleString()}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-slate-500 dark:text-slate-500 text-xs mb-1">Moderator ID:</div>
                      <div className="text-slate-900 dark:text-slate-300 font-mono text-xs">{action.adminId}</div>
                    </div>
                    {action.userId && (
                      <div>
                        <div className="text-slate-500 dark:text-slate-500 text-xs mb-1">Target User ID:</div>
                        <div className="text-slate-900 dark:text-slate-300 font-mono text-xs">{action.userId}</div>
                      </div>
                    )}
                    {action.resourceId && (
                      <div>
                        <div className="text-slate-500 dark:text-slate-500 text-xs mb-1">Resource ID:</div>
                        <div className="text-slate-900 dark:text-slate-300 font-mono text-xs">{action.resourceId}</div>
                      </div>
                    )}
                    {action.details && (
                      <div className="col-span-2">
                        <div className="text-slate-500 dark:text-slate-500 text-xs mb-1">Details:</div>
                        <div className="text-slate-900 dark:text-slate-300 text-xs">{action.details}</div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function AppealsView() {
  const [appeals, setAppeals] = useState<AppealWithDetails[]>([]);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [reviewingAppealId, setReviewingAppealId] = useState<string | null>(null);
  const [reviewStatus, setReviewStatus] = useState<'approved' | 'rejected'>('approved');
  const [reviewNotes, setReviewNotes] = useState('');

  useEffect(() => {
    loadAppeals();
  }, [filter]);

  async function loadAppeals() {
    try {
      setLoading(true);
      setError(null);
      const status = filter === 'all' ? undefined : filter;
      const data = await getAppeals(status);
      setAppeals(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load appeals');
    } finally {
      setLoading(false);
    }
  }

  async function handleReviewAppeal(appealId: string) {
    if (!reviewNotes.trim()) {
      setError('Please provide review notes');
      return;
    }

    setError(null);
    setSuccessMessage(null);

    try {
      await reviewAppeal(appealId, {
        status: reviewStatus,
        reviewNotes: reviewNotes,
      });

      setSuccessMessage(`Appeal ${reviewStatus} successfully`);
      setReviewingAppealId(null);
      setReviewNotes('');
      loadAppeals(); // Refresh list
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to review appeal');
    }
  }

  function startReview(appealId: string, defaultStatus: 'approved' | 'rejected') {
    setReviewingAppealId(appealId);
    setReviewStatus(defaultStatus);
    setReviewNotes('');
    setError(null);
    setSuccessMessage(null);
  }

  function cancelReview() {
    setReviewingAppealId(null);
    setReviewNotes('');
    setError(null);
  }

  if (loading) {
    return <div className="text-center text-slate-600 dark:text-slate-400">Loading appeals...</div>;
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 dark:bg-yellow-900/30 border-yellow-300 dark:border-yellow-800 text-yellow-700 dark:text-yellow-400';
      case 'approved':
        return 'bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-800 text-green-700 dark:text-green-400';
      case 'rejected':
        return 'bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-800 text-red-700 dark:text-red-400';
      default:
        return 'bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300';
    }
  };

  const getBanTypeColor = (banType: string) => {
    return banType === 'banned' ? 'bg-orange-100 dark:bg-orange-900/30 border-orange-300 dark:border-orange-800 text-orange-700 dark:text-orange-400' : 'bg-yellow-100 dark:bg-yellow-900/30 border-yellow-300 dark:border-yellow-800 text-yellow-700 dark:text-yellow-400';
  };

  return (
    <div>
      {/* Filter tabs */}
      <div className="mb-6 flex gap-2">
        <button
          onClick={() => setFilter('pending')}
          className={`px-4 py-2 rounded ${
            filter === 'pending' ? 'bg-brand-600 text-white' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700'
          }`}
        >
          Pending
        </button>
        <button
          onClick={() => setFilter('approved')}
          className={`px-4 py-2 rounded ${
            filter === 'approved' ? 'bg-brand-600 text-white' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700'
          }`}
        >
          Approved
        </button>
        <button
          onClick={() => setFilter('rejected')}
          className={`px-4 py-2 rounded ${
            filter === 'rejected' ? 'bg-brand-600 text-white' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700'
          }`}
        >
          Rejected
        </button>
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded ${
            filter === 'all' ? 'bg-brand-600 text-white' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700'
          }`}
        >
          All
        </button>
      </div>

      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800 rounded-lg">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-900 rounded-lg p-4 text-red-700 dark:text-red-400 mb-4">
          Error: {error}
        </div>
      )}

      <div className="mb-4 text-slate-600 dark:text-slate-400">
        Total appeals: <span className="text-slate-900 dark:text-slate-200 font-semibold">{appeals.length}</span>
      </div>

      {appeals.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-8 text-center text-slate-600 dark:text-slate-400">
          No appeals found for this filter
        </div>
      ) : (
        <div className="space-y-4">
          {appeals.map((item) => (
            <div key={item.appeal.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 border rounded text-xs font-medium ${getStatusColor(item.appeal.status)}`}>
                    {item.appeal.status.toUpperCase()}
                  </span>
                  <span className={`px-2 py-1 border rounded text-xs ${getBanTypeColor(item.banType)}`}>
                    {item.banType.toUpperCase()}
                  </span>
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-500">
                  {new Date(item.appeal.createdAt).toLocaleString()}
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <div className="text-sm text-slate-500 dark:text-slate-500 mb-1">User:</div>
                  <div className="text-slate-900 dark:text-slate-200 font-medium">{item.username}</div>
                </div>

                <div>
                  <div className="text-sm text-slate-500 dark:text-slate-500 mb-1">Original {item.banType} reason:</div>
                  <div className="text-slate-900 dark:text-slate-300 text-sm bg-slate-50 dark:bg-slate-900/50 p-2 rounded">{item.banReason}</div>
                </div>

                <div>
                  <div className="text-sm text-slate-500 dark:text-slate-500 mb-1">Appeal text:</div>
                  <div className="text-slate-900 dark:text-slate-200 bg-slate-50 dark:bg-slate-900/50 p-3 rounded">{item.appeal.appealText}</div>
                </div>

                {item.appeal.reviewedBy && item.appeal.reviewNotes && (
                  <div>
                    <div className="text-sm text-slate-500 dark:text-slate-500 mb-1">
                      Review by {item.reviewerName || 'Unknown'} on {item.appeal.reviewedAt ? new Date(item.appeal.reviewedAt).toLocaleString() : 'Unknown'}:
                    </div>
                    <div className="text-slate-900 dark:text-slate-300 text-sm bg-slate-50 dark:bg-slate-900/50 p-2 rounded">{item.appeal.reviewNotes}</div>
                  </div>
                )}

                {item.appeal.status === 'pending' && (
                  <div className="flex gap-2 mt-4">
                    {reviewingAppealId === item.appeal.id ? (
                      <div className="w-full space-y-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => setReviewStatus('approved')}
                            className={`px-4 py-2 rounded ${reviewStatus === 'approved' ? 'bg-green-600 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700'}`}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => setReviewStatus('rejected')}
                            className={`px-4 py-2 rounded ${reviewStatus === 'rejected' ? 'bg-red-600 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700'}`}
                          >
                            Reject
                          </button>
                        </div>
                        <textarea
                          value={reviewNotes}
                          onChange={(e) => setReviewNotes(e.target.value)}
                          placeholder="Enter review notes (required)..."
                          className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-slate-200 placeholder-slate-500 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                          rows={3}
                        />
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleReviewAppeal(item.appeal.id)}
                            className="bg-brand-600 hover:bg-brand-700"
                          >
                            Submit Review
                          </Button>
                          <Button
                            onClick={cancelReview}
                            variant="secondary"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <Button
                          onClick={() => startReview(item.appeal.id, 'approved')}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          Approve
                        </Button>
                        <Button
                          onClick={() => startReview(item.appeal.id, 'rejected')}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          Reject
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
